import { FC, useCallback, useMemo, useState } from "react";
import Alert from "react-bootstrap/esm/Alert";
import Table from "react-bootstrap/Table";
import Form from "react-bootstrap/esm/Form";
import Button from "react-bootstrap/esm/Button";
import { Formik, FormikHelpers } from "formik";
import { FaCheck, FaEdit, FaEnvelope } from "react-icons/fa";
import { ButtonGroup, InputGroup } from "react-bootstrap";
import { styled } from "styled-components";
import {
    ReportTestTypePrerequisiteFieldsFragment,
    ReportVersionStatus,
    ReportVersionTestTypePrerequisitesDocument,
    TestType,
    useCompleteReportTestTypePrerequisiteMutation,
    useCreateReportTestTypePrerequisiteMutation,
    useReportVersionTestTypePrerequisitesQuery,
    useUpdateReportTestTypePrerequisiteMutation,
} from "app/api/graph/types";
import { reportVersionGraphId } from "app/api/graph/helpers";
import { LoadingSpinner } from "app/component/util/LoadingSpinner";
import { useReportVersionPermissions } from "app/api/graph/hook/use-report-version-permissions";
import { nodes } from "app/util/graph";
import { useReportTestTypePrerequisitePermissions } from "app/api/graph/hook/use-report-test-type-prerequisite-permissions";
import { testTypeLabel } from "app/api/graph/strings";
import yup from "app/util/yup";
import { StatusAlert } from "app/component/form/StatusAlert";
import { handleError } from "app/api/graph/form";
import { Toolbar, ToolbarButtons, ToolbarTitle } from "app/component/util/Toolbar";
import { ButtonLink } from "app/component/button/ButtonLink";
import {
    editReportVersionTestRoute,
    reportVersionPrerequisitesRequestInformationEmailSendRoute,
} from "app/route/Routes";

const CompleteHeader = styled.th`
    width: 100px;
`;

type UpdateFormValues = {
    description: string;
};

const updateSchema: yup.ObjectSchema<UpdateFormValues> = yup.object({
    description: yup.string().required(),
});

type UpdateInputProps = {
    prerequisite: ReportTestTypePrerequisiteFieldsFragment;
    onUpdate?: () => void;
};

const UpdateInput: FC<UpdateInputProps> = ({ prerequisite, onUpdate }) => {
    const initialValues: UpdateFormValues = useMemo(
        () => ({
            description: prerequisite.description,
        }),
        [prerequisite],
    );

    const [update] = useUpdateReportTestTypePrerequisiteMutation();

    const onSubmit = (values: UpdateFormValues, actions: FormikHelpers<UpdateFormValues>) =>
        update({
            variables: {
                input: {
                    id: prerequisite.id,
                    description: values.description,
                },
            },
        })
            .then(() => {
                onUpdate?.();
            })
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <Formik validationSchema={updateSchema} onSubmit={onSubmit} initialValues={initialValues}>
            {({ handleSubmit, values, isSubmitting, errors, touched, status, handleChange, handleBlur }) => (
                <Form noValidate onSubmit={handleSubmit} autoComplete="off">
                    <StatusAlert status={status as unknown} />

                    <InputGroup className="mb-3">
                        <Form.Control
                            name="description"
                            value={values.description ?? ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.description && !!errors.description}
                            autoComplete="off"
                        />

                        <Button type="submit" disabled={isSubmitting} size="sm">
                            <FaCheck />
                        </Button>

                        <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                    </InputGroup>
                </Form>
            )}
        </Formik>
    );
};

type PrerequisiteRowProps = {
    prerequisite: ReportTestTypePrerequisiteFieldsFragment;
    isWriteable: boolean;
};

const PrerequisiteRow: FC<PrerequisiteRowProps> = ({ prerequisite, isWriteable }) => {
    const [isEditing, setEditing] = useState(false);
    const { canUpdate, canComplete } = useReportTestTypePrerequisitePermissions(prerequisite.permissions);
    const [complete] = useCompleteReportTestTypePrerequisiteMutation();

    const handleToggle = useCallback(() => {
        complete({
            variables: { input: { id: prerequisite.id, complete: !prerequisite.complete } },
            optimisticResponse: {
                completeReportTestTypePrerequisite: {
                    reportTestTypePrerequisite: {
                        ...prerequisite,
                        complete: !prerequisite.complete,
                    },
                },
            },
        }).catch((e) => {
            console.error(e);
        });
    }, [prerequisite]);

    const handleToggleEditing = useCallback(() => {
        setEditing(!isEditing);
    }, [setEditing, isEditing]);

    return (
        <tr>
            <td>
                <input
                    type="checkbox"
                    checked={prerequisite.complete}
                    disabled={!canComplete || !isWriteable}
                    onChange={handleToggle}
                />
            </td>

            <td>
                {isEditing && isWriteable ? (
                    <UpdateInput prerequisite={prerequisite} onUpdate={handleToggleEditing} />
                ) : (
                    <div className="d-flex gap-2 align-items-center">
                        <div>{prerequisite.description}</div>
                        {canUpdate && isWriteable && (
                            <Button onClick={handleToggleEditing} variant="warning" size="sm">
                                <FaEdit />
                            </Button>
                        )}
                    </div>
                )}
            </td>
        </tr>
    );
};

type CreateFormValues = {
    testType: TestType;
    description: string;
};

const createSchema: yup.ObjectSchema<CreateFormValues> = yup.object({
    testType: yup.mixed<TestType>().oneOf(Object.values(TestType)).required(),
    description: yup.string().required(),
});

type CreateRowProps = {
    versionId: number;
};

const initialValuesCreate: CreateFormValues = {
    testType: TestType.NotSpecified,
    description: "",
};

const CreateRow: FC<CreateRowProps> = ({ versionId }) => {
    const [create] = useCreateReportTestTypePrerequisiteMutation({
        refetchQueries: [ReportVersionTestTypePrerequisitesDocument],
    });

    const onSubmit = (values: CreateFormValues, actions: FormikHelpers<CreateFormValues>) =>
        create({
            variables: {
                input: {
                    reportVersion: reportVersionGraphId(versionId),
                    testType: values.testType,
                    description: values.description,
                    complete: false,
                },
            },
        })
            .then(() => {
                actions.resetForm();
            })
            .catch((error) => handleError(error, actions))
            .finally(() => actions.setSubmitting(false));

    return (
        <Formik validationSchema={createSchema} onSubmit={onSubmit} initialValues={initialValuesCreate}>
            {({ handleSubmit, values, isSubmitting, errors, touched, status, handleChange, handleBlur }) => (
                <Form noValidate onSubmit={handleSubmit} autoComplete="off">
                    <StatusAlert status={status as unknown} />

                    <Form.Group className="mb-3">
                        <Form.Control
                            name="description"
                            value={values.description ?? ""}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            isInvalid={touched.description && !!errors.description}
                            autoComplete="off"
                            placeholder="Add a new prerequisite"
                        />
                        <Form.Control.Feedback type="invalid">{errors.description}</Form.Control.Feedback>
                    </Form.Group>

                    <Button type="submit" disabled={isSubmitting}>
                        Add
                    </Button>
                </Form>
            )}
        </Formik>
    );
};

type Props = {
    reportId: number;
    versionId: number;
};

export const ReportVersionTestTypePrerequisites: FC<Props> = ({ reportId, versionId }) => {
    const { data, loading, error } = useReportVersionTestTypePrerequisitesQuery({
        variables: { versionId: reportVersionGraphId(versionId) },
    });
    const prerequisites = data?.reportVersion?.testTypePrerequisites;
    const { canCreateTestTypePrerequisite, canSendRequestInformationEmail } = useReportVersionPermissions(
        data?.reportVersion?.permissions,
    );
    const isWriteable = data?.reportVersion?.status === ReportVersionStatus.Prerequisites;

    if (loading && !prerequisites) {
        return <LoadingSpinner />;
    }
    if (error) {
        return <Alert variant="danger">{error.message}</Alert>;
    }
    if (!prerequisites) {
        return <Alert variant="danger">Not found.</Alert>;
    }

    const groups: Map<TestType, ReportTestTypePrerequisiteFieldsFragment[]> = nodes(prerequisites).reduce(
        (acc, item) => {
            const testType = item.testType;

            if (!acc.has(testType)) {
                acc.set(testType, []);
            }

            acc.get(testType)?.push(item);

            return acc;
        },
        new Map<TestType, ReportTestTypePrerequisiteFieldsFragment[]>(),
    );

    return (
        <div>
            <Toolbar>
                <ToolbarTitle>
                    <h1>Prerequisites</h1>
                </ToolbarTitle>
                <ToolbarButtons>
                    {canSendRequestInformationEmail && isWriteable ? (
                        <ButtonGroup>
                            <ButtonLink
                                to={reportVersionPrerequisitesRequestInformationEmailSendRoute.build({
                                    reportId,
                                    versionId,
                                })}
                                variant="warning"
                            >
                                <FaEnvelope /> Request information
                            </ButtonLink>
                        </ButtonGroup>
                    ) : null}
                </ToolbarButtons>
            </Toolbar>

            {!isWriteable && (
                <Alert variant="danger">
                    <p>This section is only editable during the "prerequsites" status.</p>
                    {data?.reportVersion?.status === ReportVersionStatus.Scoping && (
                        <p>
                            Please ensure that{" "}
                            <a href={editReportVersionTestRoute.build({ reportId, versionId })}>
                                the test types are configured
                            </a>{" "}
                            before transitioning from "scoping" to "prerequisites".
                        </p>
                    )}
                </Alert>
            )}

            {Array.from(groups.entries()).map(([testType, group]) => (
                <div key={testType}>
                    <h4>{testTypeLabel(testType)}</h4>

                    <Table>
                        <thead>
                            <tr>
                                <CompleteHeader>Complete</CompleteHeader>
                                <th>Prerequisite</th>
                            </tr>
                        </thead>

                        <tbody>
                            {group.map((item) => (
                                <PrerequisiteRow key={item.id} prerequisite={item} isWriteable={isWriteable} />
                            ))}
                        </tbody>
                    </Table>
                </div>
            ))}

            {canCreateTestTypePrerequisite && isWriteable && (
                <>
                    <hr />
                    <CreateRow versionId={versionId} />
                </>
            )}
        </div>
    );
};
